import React from 'react';
import { useTranslation } from 'react-i18next';

import { CreateDevice } from '@/console/features/devices/components/create-device/create-device';
import { TDeviceDrawerProps } from '@/console/features/devices/components/device-drawer/device-drawer.types';
import { DeviceSelect } from '@/console/features/devices/components/device-select/device-select';
import { useDeviceSelectOptions } from '@/console/features/devices/hooks/use-device-select-options/use-device-select-options';
import { AddExistingResources } from '@/console/features/groups/components/add-existing-resources/add-existing-resources';
import {
  TAddExistingResource,
  TAddExistingResourcesInputSchema,
} from '@/console/features/groups/components/add-existing-resources/add-existing-resources.types';
import { useAddToGroupPermission } from '@/console/features/groups/hooks/use-add-to-group-permission/use-add-to-group-permission';
import { DevicesIcon } from '@/shared/assets/icons/devices-icon';
import { MultiDrawer } from '@/shared/components/ui/multi-drawer/multi-drawer';
import { TMultiDrawerTab } from '@/shared/components/ui/multi-drawer/multi-drawer.types';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { DeviceFilterBuilder } from '@/shared/filters/entity-filters/device-filter-builder/device-filter-builder';
import { useDirectFilter } from '@/shared/filters/hooks/use-direct-filter/use-direct-filter';
import { DeviceForSelectFragment } from '@/shared/types/types.generated';

// The select calls this with its own value, undefined until something is picked.
export const getDeviceId = (resource: TAddExistingResource) =>
  (resource as DeviceForSelectFragment)?.deviceId;

export const DeviceDrawer: React.FC<TDeviceDrawerProps> = ({
  groupId,
  disableTabs,
  handlePopoverClose,
  ...rest
}) => {
  const { t } = useTranslation();
  const { can } = usePermissionCheck();
  const { createDeviceOptions } = useDeviceSelectOptions({
    additionalInfo: { deviceType: true, vendor: true },
  });
  const { hasAddPermission, isAddable } = useAddToGroupPermission({
    action: 'device:updateDevice',
    groupId,
    getResourceId: getDeviceId,
  });
  const hasCreatePermission = can('device:createDevice', groupId);
  // Server-side, so this group's devices don't fill the select's one page.
  const outsideThisGroup = useDirectFilter(new DeviceFilterBuilder(), {
    groupId: { value: groupId!, method: 'ne' },
  });

  const addDeviceTab: TMultiDrawerTab = {
    title: t('device:select_existing_devices'),
    content: (handleClose) => (
      <AddExistingResources
        typename="DeviceResponseType"
        groupId={groupId!}
        handleClose={handleClose}
        selectedItemsTitle={t('device:selected_devices')}
        getItemId={getDeviceId}
        select={
          <DeviceSelect<
            TAddExistingResourcesInputSchema,
            DeviceForSelectFragment
          >
            name="resources"
            getItemId={getDeviceId}
            // The whole fragment, so the form can tell which group it is in.
            createOption={(device) => ({
              ...createDeviceOptions(device),
              value: device,
            })}
            deviceFilter={isAddable}
            rtkQueryArgs={{ filter: outsideThisGroup }}
            required
          />
        }
      />
    ),
  };

  const createDeviceTab: TMultiDrawerTab = {
    title: t('device:create_device'),
    content: (handleClose, isOpen) => (
      <CreateDevice
        groupId={groupId}
        handleClose={handleClose}
        isOpen={isOpen}
      />
    ),
  };

  const getTabs = () => {
    const tabs: TMultiDrawerTab[] = [];
    if (groupId && hasAddPermission) {
      tabs.push(addDeviceTab);
    }
    if (hasCreatePermission) {
      tabs.push(createDeviceTab);
    }
    return tabs;
  };

  const tabs = getTabs();

  if (tabs.length === 0) {
    return null;
  }

  return (
    <MultiDrawer
      title={t('device:add_device')}
      tabs={tabs}
      componentLabel={t('device:add_device')}
      defaultTab={0}
      disableTabs={disableTabs || tabs.length === 1}
      icon={<DevicesIcon />}
      handlePopoverClose={handlePopoverClose}
      tabsFullWidth
      {...rest}
    />
  );
};
